import React from 'react';
import MenuItem from '../ui-components/MenuItem';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import {
    getIntegrationConfig,
    getSelectedCtiContactByNumber,
} from 'flow/selectors/cti-integration-selectors';
import { LOOKUP_STATUS } from 'flow/types/lookup-status-types';
import { getLookupStatusByContactsSet } from 'flow/utils/cti-status-lookup-utils';
import { List } from 'immutable';
import { getTextLabel } from 'flow/utils/string-utils';
import { handleCtiClick } from 'flow/actions/cti-actions';
import { anyOptionalKeyDown } from 'flow/utils/browser-utils';
import { selectCallRecordFromKey } from 'flow/selectors/call-selectors';
import Icon from 'flow/components/ui-components/Icon';
import useAppContext from 'flow/hooks/useAppContext';
import ContextMenu from '../ContextMenu';

interface Props {
    numberE164: string;
    callRecordKey: string;
}

const CtiHistoryEntitiesButton = ({ numberE164, callRecordKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const callRecord = useAppSelector((state) =>
        selectCallRecordFromKey(state, callRecordKey),
    );

    const direction = callRecord?.get('direction');

    const contactsMatchingNumber = useAppSelector((state) =>
        state.integration.getIn(['integrationContacts', numberE164], null),
    );

    const selectedCtiContact = useAppSelector((state) =>
        getSelectedCtiContactByNumber(state, numberE164),
    );

    const integrationConfig = useAppSelector(getIntegrationConfig);

    const myNumber = useAppSelector(
        (state) =>
            state.app.getIn(['extension', 'contact', 'fixed', 'number']) ||
            state.app.getIn(['extension', 'contact', 'mobile', 'number']),
    );

    //numberE164 => remoteNumber in E.164 format

    const defaultButtons = integrationConfig
        .get('defaultButtons', List())
        .toJS();
    const contactRouteButtons = selectedCtiContact
        ? selectedCtiContact.get('contactRouteButtons', List()).toJS()
        : [];
    const render =
        !!(defaultButtons.length || contactRouteButtons.length) &&
        getLookupStatusByContactsSet(contactsMatchingNumber) !=
            LOOKUP_STATUS.PENDING;

    const getLinkOnClick =
        (link: string, altLink: string | undefined) =>
        (event: React.MouseEvent) => {
            //if altLink exist check any if any optional key is down
            const linkToUse = !altLink
                ? link
                : anyOptionalKeyDown(event) && altLink
                  ? altLink
                  : link;
            event.preventDefault();
            event.stopPropagation();

            dispatch(
                handleCtiClick(linkToUse, myNumber, {
                    direction,
                    number: numberE164,
                    callRecordKey,
                }),
            );
        };

    const getMenuItem = ({
        label,
        link,
        altLink,
    }: {
        label: string;
        link: string;
        altLink: string | undefined;
    }) => {
        const key = `button-${link}`;
        const buttonLabel = getTextLabel(label);
        return (
            <MenuItem
                data-test-id={key.replace(':', '-')}
                key={key}
                onClick={getLinkOnClick(link, altLink)}
            >
                {buttonLabel}
            </MenuItem>
        );
    };

    const InternalMenuItems = () => {
        switch (getLookupStatusByContactsSet(contactsMatchingNumber)) {
            case LOOKUP_STATUS.SUCCESS: {
                return contactRouteButtons.length ? (
                    <div>{contactRouteButtons.map(getMenuItem)}</div>
                ) : null;
            }
            case LOOKUP_STATUS.FAILURE: {
                return defaultButtons.length ? (
                    <div>
                        <div>{defaultButtons.map(getMenuItem)}</div>
                    </div>
                ) : null;
            }
            case LOOKUP_STATUS.PENDING: {
                return null;
            }
            default:
                return null;
        }
    };

    if (!render) {
        return null;
    }

    return (
        <ContextMenu
            triggerLabel={i18next.t('cti.integration_links')}
            icon={<Icon iconKey={'extension'} />}
            className="flex size-10 items-center justify-center bg-gray-200"
        >
            <InternalMenuItems />
        </ContextMenu>
    );
};

export default React.memo(CtiHistoryEntitiesButton);
